import React, { useMemo } from 'react';
import type { TableColumnDef } from '../../../shared/table/types.ts';
import { AppDataTable } from '../../../shared/table/AppDataTable.tsx';
import { useAuditorRelianceEligible } from '../../../hooks/analytics/useAuditorReports.ts';
import { RefreshMeta } from '../controller/shared.tsx';
import type { AuditorRelianceEligibleRow } from '../../../request/analytics/auditorReports.ts';

/**
 * Report #41 — Reliance-Eligible Controls (Scoping Aid). Read-only scoping
 * checklist table for controls flagged `external_auditor_reliance = true`,
 * with their ITGC domain and highest-mapped risk level.
 */
/**
 * `typeFilter`/`significanceFilter`, when set, restrict rows using this
 * report's OWN `control_type`/`significance` columns — deliberately not
 * intersected against the Control Inventory's control-id set, since this
 * report is a broader reliance *scoping aid* (controls that could support
 * reliance) rather than the narrower already-relied-upon inventory, and
 * losing that distinction under a shared control-id filter would defeat its
 * purpose.
 */
export const RelianceEligibleTable: React.FC<{ typeFilter?: string | null; significanceFilter?: string | null }> = ({
  typeFilter,
  significanceFilter,
}) => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useAuditorRelianceEligible();
  const rows = useMemo(() => {
    let base = data ?? [];
    if (typeFilter) base = base.filter((r) => r.control_type === typeFilter);
    if (significanceFilter) base = base.filter((r) => r.significance === significanceFilter);
    return base;
  }, [data, typeFilter, significanceFilter]);

  const columns = useMemo<TableColumnDef<AuditorRelianceEligibleRow>[]>(
    () => [
      {
        id: 'control_id',
        columnLabel: 'Control',
        header: () => 'Control',
        cell: ({ row }) => <span className="font-medium text-gray-900">{row.original.control_id}</span>,
        widthPercent: 12,
      },
      {
        id: 'title',
        columnLabel: 'Title',
        header: () => 'Title',
        cell: ({ row }) => row.original.title,
        widthPercent: 28,
      },
      {
        id: 'control_type',
        columnLabel: 'Type',
        header: () => 'Type',
        cell: ({ row }) => row.original.control_type ?? '—',
        widthPercent: 12,
      },
      {
        id: 'significance',
        columnLabel: 'Significance',
        header: () => 'Significance',
        cell: ({ row }) => row.original.significance ?? '—',
        widthPercent: 12,
      },
      {
        id: 'mrc_control',
        columnLabel: 'MRC',
        header: () => 'MRC',
        cell: ({ row }) => (row.original.mrc_control ? 'Yes' : 'No'),
        widthPercent: 8,
      },
      {
        id: 'itgc_domain',
        columnLabel: 'ITGC Domain',
        header: () => 'ITGC Domain',
        cell: ({ row }) => row.original.itgc_domain ?? '—',
        widthPercent: 14,
      },
      {
        id: 'max_risk_level',
        columnLabel: 'Max Risk Level',
        header: () => 'Max Risk Level',
        cell: ({ row }) => row.original.max_risk_level ?? '—',
        widthPercent: 14,
      },
    ],
    []
  );

  return (
    <AppDataTable<AuditorRelianceEligibleRow>
      data={rows}
      columns={columns}
      loading={isLoading}
      error={isError ? error?.message ?? 'Failed to load reliance-eligible controls.' : null}
      emptyMessage="No reliance-eligible controls found."
      searchable
      searchPlaceholder="Search controls..."
      paginated
      defaultPageSize={10}
      showPageSizeSelector
      headerStart={
        <div className="flex w-full justify-end">
          <RefreshMeta updatedAt={dataUpdatedAt} onRefresh={refetch} isFetching={isFetching} />
        </div>
      }
    />
  );
};

export default RelianceEligibleTable;
